import { useEffect, useState } from "react";
import { useWarehouse } from "./useWarehouse";
import { FloorPlanUpload } from "./FloorPlanUpload";
import { CanvasLayer } from "./CanvasLayer";
import { SlotDetailPanel } from "./SlotDetailPanel";
import type { NewInventoryItem, ProductOption } from "./SlotDetailPanel";
import { Toolbar } from "./Toolbar";
import { SlotTemplatePanel } from "./SlotTemplatePanel";
import type { InventoryItem, Warehouse } from "./warehouse-types";
import {
  buildZonesFromDb,
  stripLayoutItems,
  syncWarehouseEntities,
} from "./warehouse-sync";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { Select } from "@mantine/core";
import { notification } from "antd";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconBuildingWarehouse,
  IconChevronRight,
  IconDeviceFloppy,
  IconPackage,
} from "@tabler/icons-react";

interface WarehouseManagerProps {
  initialWarehouse: Warehouse;
  products: ProductOption[];
  api: ReturnType<typeof notification.useNotification>[0];
  onSave: (warehouse: Warehouse) => void;
  saving: boolean;
}

export function WarehouseManager({
  initialWarehouse,
  products,
  api,
  onSave,
  saving,
}: WarehouseManagerProps) {
  const { t } = useTranslation();
  const {
    warehouse,
    viewState,
    currentZone,
    selectedSlot,
    selectedSlotId,
    selectedZoneId,
    selectedZone,
    tool,
    setTool,
    isPanning,
    isResizing,
    isDrawing,
    isDragging,
    currentRect,
    dragSlotPreview,
    placingSlot,
    placingCursor,
    canvasContainerRef,
    setFloorPlan,
    clearFloorPlan,
    zoomToZone,
    zoomOutToWarehouse,
    zoomIn,
    zoomOut,
    selectZone,
    selectSlot,
    setSelectedSlotId,
    clearSelection,
    renameZone,
    renameSlot,
    updateSlotItem,
    addSlotItem,
    deleteSlotItem,
    startResize,
    startDrag,
    startPan,
    startPlacingSlot,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handlePanStart,
    handlePanMove,
    handlePanEnd,
  } = useWarehouse(initialWarehouse);

  const [panelWidth, setPanelWidth] = useState(320);

  const handlePanelResizeStart = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;
    const handleMove = (ev: PointerEvent) => {
      const next = startWidth - (ev.clientX - startX);
      setPanelWidth(Math.min(Math.max(next, 280), 720));
    };
    const handleUp = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
  };

  const notifyItemError = (description: string) =>
    api.error({
      message: `Notification `,
      description: <div>{description}</div>,
      placement: "topLeft",
    });

  const handleAddItem = async (slotId: string, draft: NewInventoryItem) => {
    try {
      const created = (await single(
        orpcClient.productInventory.create([
          {
            productId: draft.productId,
            storageLocationId: slotId,
            amount: draft.quantity,
            inventoryUpperLimit: draft.limit,
          },
        ])
      )) as { id: string };
      addSlotItem(slotId, { ...draft, id: created.id });
    } catch (error) {
      console.error("Error creating product inventory:", error);
      notifyItemError(t("warehouse.itemSaveError"));
    }
  };

  const handleUpdateItem = async (
    slotId: string,
    itemId: string,
    updates: Partial<InventoryItem>,
  ) => {
    const nextQuantity = updates.quantity;
    const nextLimit = updates.limit;
    if (nextQuantity == null && nextLimit == null) return;

    const prevItem = warehouse.zones
      .flatMap((z) => z.slots)
      .find((s) => s.id === slotId)
      ?.items.find((i) => i.id === itemId);

    const optimistic: Partial<InventoryItem> = {};
    if (nextQuantity != null) optimistic.quantity = nextQuantity;
    if (nextLimit != null) optimistic.limit = nextLimit;

    const payload: {
      id: string;
      amount?: number;
      inventoryUpperLimit?: number;
    } = { id: itemId };
    if (nextQuantity != null) payload.amount = nextQuantity;
    if (nextLimit != null) payload.inventoryUpperLimit = nextLimit;

    updateSlotItem(slotId, itemId, optimistic);
    try {
      await single(orpcClient.productInventory.update([payload]));
    } catch (error) {
      console.error("Error updating product inventory:", error);
      if (prevItem) {
        const rollback: Partial<InventoryItem> = {};
        if (nextQuantity != null) rollback.quantity = prevItem.quantity;
        if (nextLimit != null) rollback.limit = prevItem.limit;
        updateSlotItem(slotId, itemId, rollback);
      }
      notifyItemError(t("warehouse.itemSaveError"));
    }
  };

  const handleDeleteItem = async (slotId: string, itemId: string) => {
    try {
      await single(orpcClient.productInventory.delete([{ id: itemId }]));
      deleteSlotItem(slotId, itemId);
    } catch (error) {
      console.error("Error deleting product inventory:", error);
      notifyItemError(t("warehouse.itemDeleteError"));
    }
  };

  // Rename a zone and persist immediately. New zones (client id "zone-…") have no
  // DB record yet, so they are only updated in memory and created on 儲存配置.
  const handleRenameZone = async (zoneId: string, name: string) => {
    const prevName = warehouse.zones.find((z) => z.id === zoneId)?.name;
    renameZone(zoneId, name);
    if (zoneId.startsWith("zone-")) return;
    try {
      await single(orpcClient.warehouseArea.update([{ id: zoneId, name }]));
    } catch (error) {
      console.error("Error renaming warehouse area:", error);
      if (prevName != null) renameZone(zoneId, prevName);
      notifyItemError(t("warehouse.renameError"));
    }
  };

  // Rename a slot and persist immediately. New slots (client id "slot-…") have no
  // DB record yet, so they are only updated in memory and created on 儲存配置.
  const handleRenameSlot = async (slotId: string, name: string) => {
    const prevName = warehouse.zones
      .flatMap((z) => z.slots)
      .find((s) => s.id === slotId)?.name;
    renameSlot(slotId, name);
    if (slotId.startsWith("slot-")) return;
    try {
      await single(orpcClient.storageLocation.update([{ id: slotId, name }]));
    } catch (error) {
      console.error("Error renaming storage location:", error);
      if (prevName != null) renameSlot(slotId, prevName);
      notifyItemError(t("warehouse.renameError"));
    }
  };

  // Determine cursor
  const getCursor = () => {
    if (isPanning) return "grabbing";
    if (isDragging) return "move";
    if (isResizing) return "default"; // Resize cursor set by handle
    if (isDrawing) return "crosshair";
    if (tool === "draw") return "crosshair";
    return "grab";
  };

  return (
    <div className="flex h-full w-full flex-col bg-[#0d0f15] text-zinc-100">
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-[#30333c] bg-[#1a1d24] px-6 py-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-400/10">
          <IconBuildingWarehouse className="h-5 w-5 text-teal-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">{t("warehouseMap.page.title")}</h1>
          <Breadcrumb
            warehouseName={warehouse.name}
            zoneName={currentZone?.name}
            slotName={selectedSlot?.name}
          />
        </div>

        <div className="ml-auto flex items-center gap-3">
          {/* Selection info */}
          {(selectedZoneId || selectedSlotId) && (
            <div className="flex items-center gap-2 rounded-lg bg-[#2a2d35] px-3 py-1.5 text-sm">
              <span className="text-zinc-400">{t("warehouseMap.page.selected")}</span>
              {selectedZoneId && (
                <span className="font-medium text-teal-400">
                  {t("warehouseMap.zoneLabel", { name: selectedZone?.name })}
                </span>
              )}
              {selectedSlotId && (
                <span className="font-medium text-blue-400">
                  {selectedSlot?.name}
                </span>
              )}
              <span className="text-xs text-zinc-400">{t("warehouseMap.page.pressDeleteHint")}</span>
            </div>
          )}

          <button
            type="button"
            onClick={() => onSave(warehouse)}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-teal-400/90 px-3 py-1.5 text-sm font-medium text-[#0d0f15] hover:bg-teal-300 disabled:opacity-60"
          >
            <IconDeviceFloppy className="h-4 w-4" />
            {saving ? t("warehouse.saving") : t("warehouse.saveLayout")}
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Canvas area */}
        <div className="relative flex-1 overflow-hidden bg-[#0d0f15]">
          <div
            ref={canvasContainerRef}
            className="relative h-full w-full touch-none overflow-hidden"
            style={{ cursor: getCursor() }}
            onPointerDown={(e) => {
              // Check for panning first (middle mouse / Alt+left)
              if (e.button === 1 || (e.button === 0 && e.altKey)) {
                handlePanStart(e);
                return;
              }

              // For left click without modifier
              if (e.button === 0 && !e.altKey) {
                const onEmpty =
                  e.target === e.currentTarget ||
                  (e.target as HTMLElement).hasAttribute("data-canvas-layer");

                if (onEmpty) {
                  // Clear selection when clicking on empty canvas
                  clearSelection();
                  // Select / floor-plan tools: drag empty space to pan the view
                  if (tool === "select" || tool === "floorplan") {
                    startPan(e.clientX, e.clientY);
                    return;
                  }
                }
                // Draw tool: drag to create a zone
                handleMouseDown(e);
              }
            }}
            onPointerMove={(e) => {
              if (isPanning) {
                handlePanMove(e);
              } else {
                handleMouseMove(e);
              }
            }}
            onPointerUp={() => {
              handlePanEnd();
              handleMouseUp();
            }}
            onPointerCancel={() => {
              handlePanEnd();
              handleMouseUp();
            }}
            onPointerLeave={() => {
              handlePanEnd();
              handleMouseUp();
            }}
          >
            {/* Canvas content */}
            <CanvasLayer
              floorPlanUrl={warehouse.floorPlanUrl}
              floorPlanRect={warehouse.floorPlanRect}
              floorPlanEditable={tool === "floorplan"}
              zones={warehouse.zones}
              viewState={viewState}
              currentRect={currentRect}
              isDrawing={isDrawing}
              selectedZoneId={selectedZoneId}
              selectedSlotId={selectedSlotId}
              dragSlotPreview={dragSlotPreview}
              onZoneClick={zoomToZone}
              onZoneSelect={selectZone}
              onSlotSelect={selectSlot}
              onStartResize={startResize}
              onStartDrag={startDrag}
            />

            {/* Upload prompt overlay when no floor plan and no zones */}
            {!warehouse.floorPlanUrl && warehouse.zones.length === 0 && (
              <FloorPlanUpload
                onUpload={setFloorPlan}
                hasFloorPlan={!!warehouse.floorPlanUrl}
              />
            )}

            {/* Toolbar */}
            <Toolbar
              viewState={viewState}
              zones={warehouse.zones}
              hasFloorPlan={!!warehouse.floorPlanUrl}
              tool={tool}
              onToolChange={(nextTool) => {
                clearSelection();
                setTool(nextTool);
              }}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onZoomToWarehouse={zoomOutToWarehouse}
              onUploadFloorPlan={setFloorPlan}
              onClearFloorPlan={clearFloorPlan}
            />
          </div>
        </div>

        {/* Right panel - only shown when it has content (slot template / slot
            detail) so the canvas spans full width in warehouse overview.
            Drag the left edge to widen/narrow it. */}
        {((viewState.mode === "zone" && !selectedSlotId) || selectedSlotId) && (
          <div
            className="relative flex flex-col border-l border-[#30333c] bg-[#1a1d24]"
            style={{ width: panelWidth }}
          >
            {/* Resize handle */}
            <div
              onPointerDown={handlePanelResizeStart}
              className="absolute left-0 top-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-col-resize touch-none [@media(pointer:coarse)]:w-5 bg-transparent transition-colors hover:bg-teal-400/40"
            />

            {viewState.mode === "zone" && !selectedSlotId && (
              <SlotTemplatePanel
                onStartPlacing={startPlacingSlot}
                zoneName={currentZone?.name || ""}
                onRename={(name) => {
                  if (currentZone) handleRenameZone(currentZone.id, name);
                }}
                slots={currentZone?.slots ?? []}
                selectedSlotId={selectedSlotId}
                onSelectSlot={selectSlot}
              />
            )}

            {selectedSlotId && (
              <SlotDetailPanel
                slot={selectedSlot}
                products={products}
                onUpdateItem={handleUpdateItem}
                onAddItem={handleAddItem}
                onDeleteItem={handleDeleteItem}
                onRename={handleRenameSlot}
                onClose={() => setSelectedSlotId(null)}
              />
            )}
          </div>
        )}
      </div>

      {placingSlot && placingCursor && (
        <div
          className="pointer-events-none fixed z-50 flex items-center gap-2 rounded-lg border-2 border-dashed border-blue-400 bg-blue-400/20 px-3 py-2 shadow-lg backdrop-blur-sm"
          style={{ left: placingCursor.x + 14, top: placingCursor.y + 14 }}
        >
          <IconPackage className="h-4 w-4 text-blue-400" />
          <span className="text-xs font-medium text-zinc-100">{t("warehouseMap.newSlot")}</span>
        </div>
      )}
    </div>
  );
}

interface BreadcrumbProps {
  warehouseName: string;
  zoneName?: string;
  slotName?: string;
}

function Breadcrumb({ warehouseName, zoneName, slotName }: BreadcrumbProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 text-sm text-zinc-400">
      <span>{warehouseName}</span>
      {zoneName && (
        <>
          <IconChevronRight className="h-3 w-3" />
          <span className="text-teal-400">{t("warehouseMap.zoneLabel", { name: zoneName })}</span>
        </>
      )}
      {slotName && (
        <>
          <IconChevronRight className="h-3 w-3" />
          <span className="text-blue-400">{slotName}</span>
        </>
      )}
    </div>
  );
}

export default function WarehouseManagementSystemPage() {
  const { t } = useTranslation();
  const { id: routeId } = useParams();
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [initialWarehouse, setInitialWarehouse] = useState<Warehouse | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.warehouse.list({});
        setWarehouses((result as any) || []);
      } catch (error) {
        console.error("Error fetching warehouses:", error);
      }
    };

    fetchData();
  }, []);

  const loadWarehouse = async (value: string | null) => {
    setSelectedId(value);
    setInitialWarehouse(null);
    if (!value) return;

    try {
      const result = await single(orpcClient.warehouse.read([{ id: value }]));
      const layout = (result?.layoutConfig as Warehouse | null) || null;
      const [dbAreas, inventories, productList] = await Promise.all([
        orpcClient.warehouseArea.list({
          filter: { warehouseId: value },
        }) as Promise<any[]>,
        orpcClient.productInventory.list({}) as Promise<any[]>,
        orpcClient.product.list({}) as Promise<any[]>,
      ]);

      const productById = new Map(productList.map((p) => [p.id, p]));
      setProducts(
        productList.map((p) => ({ id: p.id, name: p.name })),
      );

      const itemsByLocationId = new Map<string, InventoryItem[]>();
      for (const inv of inventories || []) {
        const list = itemsByLocationId.get(inv.storageLocationId) ?? [];
        list.push({
          id: inv.id,
          productId: inv.productId,
          name: productById.get(inv.productId)?.name ?? inv.productId,
          quantity: inv.amount,
          limit: inv.inventoryUpperLimit,
        });
        itemsByLocationId.set(inv.storageLocationId, list);
      }

      setInitialWarehouse({
        id: value,
        name: result?.name || "",
        floorPlanUrl: layout?.floorPlanUrl ?? null,
        floorPlanRect: layout?.floorPlanRect,
        zones: buildZonesFromDb(dbAreas, layout?.zones ?? [], itemsByLocationId),
      });
    } catch (error) {
      console.error("Error loading warehouse layout:", error);
      api.error({
        message: `Notification `,
        description: <div>{t("warehouse.loadLayoutError")}</div>,
        placement: "topLeft",
      });
    }
  };

  // Auto-select the warehouse from the route param (when opened from a warehouse)
  useEffect(() => {
    if (routeId) loadWarehouse(routeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const handleSave = async (warehouse: Warehouse) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const synced = await syncWarehouseEntities(selectedId, warehouse);
      await single(
        orpcClient.warehouse.update([
          {
            id: selectedId,
            layoutConfig: stripLayoutItems(synced),
          },
        ])
      );
      setInitialWarehouse(synced);
      setReloadKey((k) => k + 1);
      api.success({
        message: `Notification `,
        description: <div>{t("warehouse.layoutSaved")}</div>,
        placement: "topLeft",
      });
    } catch (error) {
      console.error("Error saving warehouse layout:", error);
      api.error({
        message: `Notification `,
        description: <div>{t("warehouse.layoutSaveError")}</div>,
        placement: "topLeft",
      });
    } finally {
      setSaving(false);
    }
  };

  if (routeId) {
    return (
      <div className="h-screen w-screen overflow-hidden">
        {contextHolder}
        {selectedId && initialWarehouse ? (
          <WarehouseManager
            key={`${selectedId}-${reloadKey}`}
            initialWarehouse={initialWarehouse}
            products={products}
            api={api}
            onSave={handleSave}
            saving={saving}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#0d0f15] text-center">
            <div className="rounded-full bg-[#2a2d35] p-4">
              <IconBuildingWarehouse className="h-8 w-8 text-zinc-400" />
            </div>
            <p className="text-sm text-zinc-400">
              {t("warehouse.selectWarehousePrompt")}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-3">
      {contextHolder}
      <div className="mb-3 flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <span className="text-sm font-medium text-gray-600">
          {t("warehouse.selectWarehouse")}
        </span>
        <Select
          className="w-64"
          placeholder={t("warehouse.selectWarehouse")}
          value={selectedId}
          onChange={loadWarehouse}
          data={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          leftSection={<IconBuildingWarehouse size={16} />}
          searchable
          clearable
        />
      </div>

      <div className="h-[calc(100vh-210px)] min-h-[520px] overflow-hidden rounded-xl border border-[#30333c] shadow-lg">
        {selectedId && initialWarehouse ? (
          <WarehouseManager
            key={`${selectedId}-${reloadKey}`}
            initialWarehouse={initialWarehouse}
            products={products}
            api={api}
            onSave={handleSave}
            saving={saving}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-[#0d0f15] text-center">
            <div className="rounded-full bg-[#2a2d35] p-4">
              <IconBuildingWarehouse className="h-8 w-8 text-zinc-400" />
            </div>
            <p className="text-sm text-zinc-400">
              {t("warehouse.selectWarehousePrompt")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
